<template>
    <div class="cfg-wrap" :class="{ 'cfg-fullscreen': isFullscreen }">
        <!-- ── Toolbar ───────────────────────────────────────────────── -->
        <div v-if="hasGraph" class="cfg-toolbar">
            <div class="cfg-tb-left">
                <!-- Canvas facts only. The page's stat strip owns the data
                     totals; repeating them here invited two different numbers
                     for the same thing (drawn vs. distinct members). -->
                <span class="cfg-readout">
                    <span class="cfg-readout-cell">
                        <b>{{ stats.nodes }}</b><i>nodes drawn</i>
                    </span>
                    <span class="cfg-readout-cell">
                        <b>{{ stats.edges }}</b><i>edges</i>
                    </span>
                    <span
                        v-if="stats.hiddenTypes"
                        class="cfg-readout-cell"
                        title="Neighbour types hidden with the legend"
                    >
                        <b>{{ stats.hiddenTypes }}</b><i>types hidden</i>
                    </span>
                </span>
            </div>
            <div class="cfg-tb-right">
                <button
                    class="cfg-tb-btn"
                    :class="{ 'is-on': showOrbits }"
                    title="Toggle column labels"
                    @click="toggleOrbits"
                ><i class="fa fa-table-columns"></i></button>
                <div class="cfg-tb-sep"></div>
                <button class="cfg-tb-btn" title="Zoom in" @click="zoomIn"><i class="fa fa-magnifying-glass-plus"></i></button>
                <button class="cfg-tb-btn" title="Zoom out" @click="zoomOut"><i class="fa fa-magnifying-glass-minus"></i></button>
                <button class="cfg-tb-btn" title="Fit to screen" @click="fitToScreen"><i class="fa fa-down-left-and-up-right-to-center"></i></button>
                <button class="cfg-tb-btn" title="Re-run layout (releases anything you dragged)" @click="resetView"><i class="fa fa-rotate-left"></i></button>
                <div class="cfg-tb-sep"></div>
                <button
                    class="cfg-tb-btn"
                    :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
                    @click="toggleFullscreen"
                ><i :class="isFullscreen ? 'fa fa-compress' : 'fa fa-expand'"></i></button>
            </div>
        </div>

        <!-- ── Legend ────────────────────────────────────────────────── -->
        <div v-if="hasGraph" class="cfg-legend">
            <span class="cfg-legend-static" title="The entity you looked up">
                <span class="cfg-legend-dot cfg-legend-dot--root"></span>Your entity
            </span>
            <span class="cfg-legend-static" title="A cluster this entity belongs to — a thick line means the cluster lists your entity as a member, a thin line means it does not">
                <span class="cfg-legend-dot cfg-legend-dot--cluster"></span>Cluster
            </span>
            <span
                class="cfg-legend-static"
                title="A cluster member that is also in flatten_neighbors_array — the dashed line runs back to your entity"
            >
                <span class="cfg-legend-swatch-link"></span>Also your neighbour
            </span>
            <span
                class="cfg-legend-static"
                title="A direct neighbour of your entity from flatten_neighbors_array, drawn as a node off your entity"
            >
                <span class="cfg-legend-swatch-own"></span>Node
            </span>
            <span
                v-if="enableNodeMove"
                class="cfg-legend-static"
                title="Moved onto this cluster — not saved yet"
            >
                <span class="cfg-legend-dot cfg-legend-dot--moved"></span>Moved
            </span>
            <span class="cfg-legend-div"></span>
            <template v-for="item in visibleLegendItems" :key="item.type">
                <button
                    v-if="!item.legendOverflow"
                    type="button"
                    class="cfg-legend-chip"
                    :class="{ 'is-off': hiddenTypes.has(item.type) }"
                    :title="`${item.label} — click to hide/show`"
                    @click="toggleType(item.type)"
                >
                    <span class="cfg-legend-dot" :style="{ background: hiddenTypes.has(item.type) ? '#b7bec9' : item.color }"></span>
                    <span class="cfg-legend-label">{{ item.label }}</span>
                    <span class="cfg-legend-cnt">{{ item.count }}</span>
                </button>
                <button v-else type="button" class="cfg-legend-chip cfg-legend-more" @click="legendExpanded = !legendExpanded">
                    {{ item.label }}
                    <i :class="legendExpanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                </button>
            </template>
        </div>

        <!-- ── Canvas ────────────────────────────────────────────────── -->
        <div class="cfg-canvas-area">
            <transition name="cfg-insp">
                <div v-if="hasGraph && inspector.visible" class="cfg-inspector" role="status" aria-live="polite">
                    <span class="cfg-insp-chip" :style="{ '--ic': inspector.color }">
                        <span class="cfg-insp-dot"></span>
                        <span class="cfg-insp-label">{{ inspector.label }}</span>
                    </span>
                    <span class="cfg-insp-meta">
                        <template v-for="(ln, i) in inspector.lines" :key="i">
                            <span v-if="i > 0" class="cfg-insp-sep">·</span>{{ ln }}
                        </template>
                    </span>
                </div>
            </transition>

            <div ref="graphContainer" class="cfg-canvas"></div>

            <div v-if="!hasGraph" class="cfg-empty">
                <i class="fa fa-circle-nodes"></i>
                <p>{{ emptyText }}</p>
            </div>
        </div>

        <!-- ── Detail panel ──────────────────────────────────────────── -->
        <transition name="cfg-slide">
            <div v-if="detailNode" class="cfg-panel" :class="{ 'cfg-panel--wide': panelIsWide }">
                <div class="cfg-panel-hdr" :style="{ '--node-color': nodeFill(detailNode) }">
                    <span class="cfg-panel-badge"><i :class="panelIcon(detailNode)"></i></span>
                    <span class="cfg-panel-title">{{ panelTitle(detailNode) }}</span>
                    <button class="cfg-panel-close" aria-label="Close" @click="detailNode = null"><i class="fa fa-xmark"></i></button>
                </div>

                <div class="cfg-panel-body">
                    <!-- Root -->
                    <template v-if="detailNode.type === 'root'">
                        <div class="cfg-kv"><span>Type</span><span>{{ detailNode.entityType }}</span></div>
                        <div class="cfg-kv"><span>Domain</span><span>{{ detailNode.entityDomain }}</span></div>
                        <div class="cfg-sec-lbl">Entity ID</div>
                        <div class="cfg-mono-val">{{ detailNode.entityId }}</div>
                        <div class="cfg-stat-grid">
                            <div class="cfg-stat-cell"><span>Clusters</span><b>{{ detailNode.clusterCount }}</b></div>
                            <div class="cfg-stat-cell"><span>Direct neighbours</span><b>{{ detailNode.flattenCount }}</b></div>
                        </div>
                    </template>

                    <!-- Cluster -->
                    <template v-else-if="detailNode.type === 'cluster'">
                        <div class="cfg-pill-row">
                            <span class="cfg-type-pill">{{ detailNode.entityType }}~{{ detailNode.entityDomain }}</span>
                            <span v-if="detailNode.includesSelf" class="cfg-flag cfg-flag--teal">Contains your entity</span>
                            <span v-if="detailNode.isShared" class="cfg-flag cfg-flag--amber">
                                <i class="fa fa-link me-1"></i>{{ detailNode.sharedCount }} also your neighbour<span v-if="detailNode.sharedCount !== 1">s</span>
                            </span>
                        </div>
                        <div class="cfg-sec-lbl">Cluster ID</div>
                        <div class="cfg-mono-val">{{ detailNode.entityId }}</div>
                        <p v-if="detailNode.includesSelf" class="cfg-panel-note">
                            Your entity is one of this cluster's own members — that is what makes it your
                            cluster. It is not drawn a second time here.
                        </p>
                        <div class="cfg-stat-grid">
                            <div class="cfg-stat-cell"><span>Members</span><b>{{ detailNode.neighborCount }}</b></div>
                            <div class="cfg-stat-cell"><span>On graph</span><b>{{ detailNode.visibleCount }}</b></div>
                            <div class="cfg-stat-cell"><span>Also yours</span><b class="cfg-amber">{{ detailNode.sharedCount }}</b></div>
                        </div>
                        <template v-if="(detailNode.sharedIds || []).length">
                            <div class="cfg-sec-lbl">Neighbours you also have</div>
                            <div class="cfg-id-list">
                                <div v-for="(id, i) in detailNode.sharedIds" :key="'b' + i" class="cfg-id-row cfg-id-row--shared">{{ id }}</div>
                            </div>
                        </template>
                        <div class="cfg-btn-row">
                            <button
                                v-if="(detailNode.neighborIds || []).length"
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="download(`${detailNode.entityId}-neighbours.txt`, detailNode.neighborIds.join('\n'))"
                            ><i class="fa fa-download me-1"></i>Download {{ detailNode.neighborIds.length }} neighbours</button>
                        </div>
                    </template>

                    <!-- Connector: the link between your entity and a cluster -->
                    <template v-else-if="detailNode.type === 'connector'">
                        <div class="cfg-pill-row">
                            <span class="cfg-type-pill">{{ detailNode.neighborType }}</span>
                            <span class="cfg-flag cfg-flag--amber">{{ (detailNode.entityIds || []).length }} shared</span>
                        </div>
                        <p class="cfg-panel-note">
                            Members of <span class="cfg-mono-inline">{{ detailNode.clusterKey }}</span> that are
                            also in your entity's neighbour list. The dashed line runs from each of them back to
                            your entity — that is what ties the two together.
                        </p>
                        <div class="cfg-id-list">
                            <div v-for="(id, i) in groupIdsDisplayed" :key="'c' + i" class="cfg-id-row cfg-id-row--shared">{{ id }}</div>
                        </div>
                        <div class="cfg-btn-row">
                            <button
                                v-if="(detailNode.entityIds || []).length > groupIdsDisplayed.length || groupShowAll"
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="groupShowAll = !groupShowAll"
                            >{{ groupShowAll ? 'Show preview' : `Show all ${detailNode.entityIds.length}` }}</button>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="download(`${detailNode.neighborType}-links.txt`, detailNode.entityIds.join('\n'))"
                            ><i class="fa fa-download me-1"></i>Download</button>
                        </div>
                    </template>

                    <!-- Neighbour group -->
                    <template v-else-if="detailNode.type === 'entity_group'">
                        <div class="cfg-pill-row">
                            <span class="cfg-type-pill">{{ detailNode.neighborType }}</span>
                            <span class="cfg-cnt-badge">{{ (detailNode.entityIds || []).length }} IDs</span>
                        </div>
                        <p v-if="detailNode.clusterKey" class="cfg-panel-note">
                            Member of <span class="cfg-mono-inline">{{ detailNode.clusterKey }}</span>
                        </p>
                        <p v-else-if="detailNode.isOwnGroup" class="cfg-panel-note cfg-panel-note--teal">
                            Direct neighbours of your entity, drawn as nodes off your entity.
                        </p>
                        <div class="cfg-id-list">
                            <div
                                v-for="(id, i) in groupIdsDisplayed"
                                :key="'g' + i"
                                class="cfg-id-row"
                                :class="{ 'cfg-id-row--shared': (detailNode.sharedIds || []).includes(id) }"
                            >{{ id }}</div>
                        </div>
                        <div class="cfg-btn-row">
                            <button
                                v-if="(detailNode.entityIds || []).length > groupIdsDisplayed.length || groupShowAll"
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="groupShowAll = !groupShowAll"
                            >{{ groupShowAll ? 'Show preview' : `Show all ${detailNode.entityIds.length}` }}</button>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="download(`${detailNode.neighborType}-ids.txt`, detailNode.entityIds.join('\n'))"
                            ><i class="fa fa-download me-1"></i>Download</button>
                        </div>
                    </template>

                    <!-- Collapsed bundle -->
                    <template v-else-if="detailNode.type === 'entity_bundle'">
                        <p class="cfg-panel-note">Only the first slice of a long neighbour list is drawn; raise the limit above the graph to show more.</p>
                        <div class="cfg-stat-grid">
                            <div class="cfg-stat-cell"><span>Total</span><b>{{ detailNode.bundleTotal }}</b></div>
                            <div class="cfg-stat-cell"><span>On graph</span><b>{{ detailNode.bundleDisplayed }}</b></div>
                            <div class="cfg-stat-cell"><span>Collapsed</span><b class="cfg-amber">{{ detailNode.bundleOverflow }}</b></div>
                        </div>
                        <div class="cfg-sec-lbl">Collapsed IDs</div>
                        <div class="cfg-id-list">
                            <div v-for="(id, i) in bundleIdsDisplayed" :key="'x' + i" class="cfg-id-row">{{ id }}</div>
                        </div>
                        <div class="cfg-btn-row">
                            <button
                                v-if="detailNode.bundleHiddenNotListed > 0"
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="groupShowAll = !groupShowAll"
                            >{{ groupShowAll ? 'Show preview' : `Show all ${detailNode.bundleHiddenAllIds.length}` }}</button>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="download('collapsed-ids.txt', detailNode.bundleHiddenAllIds.join('\n'))"
                            ><i class="fa fa-download me-1"></i>Download</button>
                        </div>
                    </template>

                    <!-- Single neighbour -->
                    <template v-else>
                        <div class="cfg-pill-row">
                            <span class="cfg-type-pill">{{ detailNode.neighborType }}</span>
                            <span v-if="detailNode.isShared" class="cfg-flag cfg-flag--amber"><i class="fa fa-link me-1"></i>Also your neighbour</span>
                        </div>
                        <div class="cfg-sec-lbl">Neighbour ID</div>
                        <div class="cfg-mono-val">{{ detailNode.entityId }}</div>
                        <p v-if="detailNode.clusterKey" class="cfg-panel-note">
                            Member of <span class="cfg-mono-inline">{{ detailNode.clusterKey }}</span>
                        </p>
                        <p v-if="detailNode.isSelf" class="cfg-panel-note">
                            This is the entity you looked up — the cluster contains it as one of its own
                            members.
                        </p>
                        <p v-if="detailNode.isOwnOnly" class="cfg-panel-note cfg-panel-note--teal">
                            A direct neighbour of your entity, drawn as a node.
                        </p>
                        <p v-if="detailNode.isShared" class="cfg-panel-note cfg-panel-note--amber">
                            This is also a direct neighbour of your entity, so it is where your entity and
                            this cluster actually touch.
                        </p>
                    </template>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import * as d3 from 'd3'

const LEGEND_MAX = 8
const GROUP_ID_PREVIEW = 60

/* Three structural roles get fixed, well-separated hues. Neighbour types are
   generated below so any number of type~domain pairs stays distinguishable. */
const ROOT_COLOR = '#0f766e' /* teal-700   — the entity under investigation */
const CLUSTER_COLOR = '#6f42c1' /* violet-600 — matches the Graph Curation page */
const SHARED_COLOR = '#d97706' /* amber-600 — marks a neighbour the root also has */
const MOVED_COLOR = '#10b981' /* emerald-500 — a member moved here, not yet saved */

const FA_ICON = {
    root: 'fa fa-crosshairs',
    cluster: 'fa fa-users',
    entity_group: 'fa fa-layer-group',
    connector: 'fa fa-link',
    entity_bundle: 'fa fa-database',
    neighbor: 'fa fa-fingerprint',
}

/* Font Awesome 6 Solid code points, drawn as SVG text inside each node. */
const GLYPH = {
    root: '',
    cluster: '',
    entity_group: '',
    entity_bundle: '',
    neighbor: '',
}

/** Golden-angle hue walk — every index gets a maximally distinct colour. */
function goldenColor(i) {
    return `hsl(${((i * 137.508) % 360).toFixed(1)}, 62%, 52%)`
}

let _uid = 0

export default {
    name: 'D3ClusterFormationGraph',
    props: {
        data: { type: Object, default: null },
        emptyText: { type: String, default: 'Run a lookup to draw the cluster formation.' },
        /* Merge curation: dropping a member on another cluster emits `node-moved`. */
        enableNodeMove: { type: Boolean, default: false },
    },
    emits: ['node-select', 'node-moved'],
    data() {
        return {
            uid: `cfg${++_uid}`,
            hasGraph: false,
            isFullscreen: false,
            showOrbits: true,
            legendExpanded: false,
            hiddenTypes: new Set(),
            /* Groups the user opened into individual ID circles. */
            expandedIds: new Set(),
            /* Nodes the user dragged, by id -> {x, y}. drawGraph works on
               copies of the node objects, so a pin has to live here to survive
               a redraw. */
            pinnedPos: new Map(),
            /* Individual circles drawn per opened group before the rest fold into a bundle. */
            expandCap: 24,
            legendItems: [],
            stats: { nodes: 0, edges: 0, hiddenTypes: 0 },
            inspector: { visible: false, label: '', color: SHARED_COLOR, lines: [] },
            inspectorTimer: null,
            detailNode: null,
            groupShowAll: false,
            simulation: null,
            svg: null,
            zoomBehavior: null,
            nodes: [],
            edges: [],
            colorByType: new Map(),
        }
    },
    computed: {
        isDark() {
            return this.$store.state.darkMode
        },
        visibleLegendItems() {
            const items = this.legendItems
            if (this.legendExpanded) {
                return items.length > LEGEND_MAX
                    ? items.concat([{ type: '__c__', legendOverflow: true, label: 'Show less' }])
                    : items
            }
            if (items.length <= LEGEND_MAX) return items
            return items
                .slice(0, LEGEND_MAX)
                .concat([{ type: '__o__', legendOverflow: true, label: `+${items.length - LEGEND_MAX} more` }])
        },
        panelIsWide() {
            const t = this.detailNode && this.detailNode.type
            return t === 'entity_group' || t === 'entity_bundle' || t === 'cluster'
        },
        groupIdsDisplayed() {
            const d = this.detailNode
            if (!d || !d.entityIds) return []
            return this.groupShowAll ? d.entityIds : d.entityIds.slice(0, GROUP_ID_PREVIEW)
        },
        bundleIdsDisplayed() {
            const d = this.detailNode
            if (!d || !d.bundleHiddenAllIds) return []
            return this.groupShowAll ? d.bundleHiddenAllIds : d.bundleHiddenPreview || []
        },
    },
    watch: {
        detailNode() {
            this.groupShowAll = false
        },
        data: {
            handler(v) {
                if (v && v.nodes && v.nodes.length) this.$nextTick(() => this.loadGraph(v))
                else this.clearGraph()
            },
            deep: true,
        },
        isDark() {
            if (this.hasGraph) this.$nextTick(() => this.drawGraph())
        },
    },
    mounted() {
        if (this.data && this.data.nodes && this.data.nodes.length) {
            this.$nextTick(() => this.loadGraph(this.data))
        }
        if (typeof ResizeObserver !== 'undefined' && this.$refs.graphContainer) {
            let t = null
            this._ro = new ResizeObserver(() => {
                clearTimeout(t)
                t = setTimeout(() => {
                    if (this.svg && this.hasGraph) this.drawGraph()
                }, 160)
            })
            this._ro.observe(this.$refs.graphContainer)
        }
    },
    beforeUnmount() {
        if (this.simulation) this.simulation.stop()
        if (this._ro) {
            this._ro.disconnect()
            this._ro = null
        }
        clearTimeout(this.inspectorTimer)
    },
    methods: {
        /* ── Colour / label helpers ───────────────────────────────── */
        nodeFill(d) {
            if (!d) return '#64748b'
            if (d.type === 'root') return ROOT_COLOR
            if (d.type === 'cluster') return CLUSTER_COLOR
            if (d.type === 'entity_bundle') return this.isDark ? '#475569' : '#94a3b8'
            /* Amber: this node is the link between your entity and a cluster. */
            if (d.type === 'connector') return SHARED_COLOR
            const key = String(d.neighborType || d.key || '').toLowerCase()
            return this.colorByType.get(key) || '#64748b'
        },
        nodeRadius(d) {
            if (d.type === 'root') return 30
            if (d.type === 'cluster') return 17 + Math.min(15, Math.sqrt(Number(d.neighborCount) || 1) * 1.6)
            if (d.type === 'connector') return 15 + Math.min(11, Math.log2((Number(d.idCountInNode) || 1) + 1) * 2.4)
            if (d.type === 'entity_group') return 12 + Math.min(11, Math.log2((Number(d.idCountInNode) || 1) + 1) * 2.4)
            if (d.type === 'entity_bundle') return 15
            return d.isShared ? 10 : 8
        },
        edgeColor(e) {
            if (e.linkKind === 'link' || e.linkKind === 'direct') return SHARED_COLOR
            /* Your own branch — neighbours no cluster holds. Teal ties them to
               the root rather than to any cluster. */
            if (e.linkKind === 'own') return ROOT_COLOR
            return this.isDark ? '#8a97a8' : '#aab6c6'
        },
        panelIcon(d) {
            return FA_ICON[d.type] || FA_ICON.neighbor
        },
        panelTitle(d) {
            if (d.type === 'root') return 'Root entity'
            if (d.type === 'cluster') return 'Cluster'
            if (d.type === 'entity_bundle') return d.isOwnOnly ? 'Your neighbours not shown' : 'Neighbours not shown'
            if (d.type === 'connector') return 'What links them'
            if (d.type === 'entity_group') return d.isOwnGroup ? 'Nodes' : "Cluster's neighbours"
            return d.isOwnOnly ? 'Your neighbour' : 'Neighbour'
        },
        /** Top caption line: the type~domain the node belongs to. */
        typeDomainLabel(d) {
            if (d.type === 'entity_bundle') return 'more neighbours'
            const nt = String(d.neighborType || '')
            if (nt) return nt
            const p = String(d.key || '').split('~')
            return p.length >= 2 ? `${p[0]}~${p[1]}` : p[0] || ''
        },
        /** Bottom caption line: the ID itself, elided in the middle if long. */
        idLabel(d) {
            if (d.type === 'entity_bundle') return `+${d.bundleOverflow} not shown`
            if (d.type === 'connector') {
                const n = (d.entityIds || []).length
                return `${n} member${n === 1 ? '' : 's'} · also yours`
            }
            if (d.type === 'entity_group') {
                const n = (d.entityIds || []).length
                if (d.isOwnGroup) return `${n} node${n === 1 ? '' : 's'}`
                return `${n} neighbour${n === 1 ? '' : 's'}`
            }
            const id = String(d.entityId || d.key || '')
            return id.length > 20 ? `${id.slice(0, 11)}…${id.slice(-6)}` : id
        },
        shortLabel(d) {
            const td = this.typeDomainLabel(d)
            const id = this.idLabel(d)
            return td && id ? `${td} · ${id}` : td || id
        },

        /* ── Load ─────────────────────────────────────────────────── */
        clearGraph() {
            if (this.simulation) this.simulation.stop()
            this.hasGraph = false
            this.detailNode = null
            this.nodes = []
            this.edges = []
            this.legendItems = []
            const c = this.$refs.graphContainer
            if (c) d3.select(c).select('svg').remove()
        },
        loadGraph(input) {
            /* A staged move rebuilds the graph. Groups the user opened stay open —
               matched by what they hold, since group ids are positional — so single
               IDs can be moved one after another. */
            const openSig = (n) => `${n.type}|${n.clusterKey || ''}|${n.neighborType || n.key || ''}`
            const wasOpen = new Set(this.nodes.filter((n) => this.expandedIds.has(n.id)).map(openSig))
            const wasHidden = this.hiddenTypes
            this.detailNode = null
            this.pinnedPos = new Map()
            this.nodes = input.nodes.map((n) => ({ ...n }))
            this.expandedIds = new Set(this.nodes.filter((n) => wasOpen.has(openSig(n))).map((n) => n.id))
            this.edges = input.edges.map((e) => ({
                source: typeof e.source === 'object' ? e.source.id : e.source,
                target: typeof e.target === 'object' ? e.target.id : e.target,
                linkKind: e.linkKind,
            }))

            /* Assign a colour per unique neighbour type~domain, in first-seen order. */
            const map = new Map()
            let i = 0
            this.nodes.forEach((n) => {
                if (n.type === 'root' || n.type === 'cluster' || n.type === 'entity_bundle') return
                const k = String(n.neighborType || n.key || '').toLowerCase()
                if (k && !map.has(k)) map.set(k, goldenColor(i++))
            })
            this.colorByType = map
            this.hiddenTypes = new Set([...wasHidden].filter((t) => map.has(t)))

            this.hasGraph = true
            this.updateStats()
            this.$nextTick(() => this.drawGraph())
        },
        updateStats() {
            const vis = this.visibleNodes()
            const ids = new Set(vis.map((n) => n.id))
            this.stats = {
                nodes: vis.length,
                edges:
                    this.edges.filter((e) => ids.has(this.eid(e.source)) && ids.has(this.eid(e.target))).length +
                    this.expansionEdges(vis).length,
                hiddenTypes: this.hiddenTypes.size,
            }

            /* Count distinct IDs, not nodes. The same neighbour is drawn twice
               when it is both yours and a cluster's, and summing node counts
               reported 198 dev~aut where the data holds 99. */
            const idsByType = new Map()
            this.nodes.forEach((n) => {
                if (n.type === 'root' || n.type === 'cluster' || n.type === 'entity_bundle') return
                const k = String(n.neighborType || n.key || '').toLowerCase()
                if (!k) return
                if (!idsByType.has(k)) idsByType.set(k, new Set())
                const bucket = idsByType.get(k)
                const ids = (n.entityIds && n.entityIds.length ? n.entityIds : [n.key]).filter(Boolean)
                ids.forEach((id) => bucket.add(String(id)))
            })
            this.legendItems = [...idsByType.entries()]
                .map(([type, ids]) => ({ type, count: ids.size, label: type, color: this.colorByType.get(type) || '#64748b' }))
                .sort((a, b) => b.count - a.count)
        },
        eid(v) {
            return typeof v === 'object' ? v.id : v
        },
        /** Member keys a dragged node stands for; empty for nodes that cannot be moved. */
        moveKeysOf(d) {
            if (!d) return []
            if (d.type === 'neighbor') return d.key ? [d.key] : []
            if (d.type === 'entity_group' || d.type === 'connector') return d.entityIds || []
            if (d.type === 'entity_bundle') return d.bundleHiddenAllIds || []
            return []
        },
        visibleNodes() {
            const base = !this.hiddenTypes.size
                ? this.nodes
                : this.nodes.filter((n) => {
                      if (n.type === 'root' || n.type === 'cluster') return true
                      const k = String(n.neighborType || n.key || '').toLowerCase()
                      return !this.hiddenTypes.has(k)
                  })
            if (!this.expandedIds.size) return base
            return base.concat(this.expansionNodes(base))
        },
        /** Individual ID circles for every group the user has opened. */
        expansionNodes(base) {
            const out = []
            base.forEach((g) => {
                if ((g.type !== 'entity_group' && g.type !== 'connector') || !this.expandedIds.has(g.id)) return
                const ids = g.entityIds || []
                const shown = ids.slice(0, this.expandCap)
                shown.forEach((v) => {
                    const p = String(v).split('~')
                    out.push({
                        id: `${g.id}|x|${v}`,
                        type: 'neighbor',
                        level: (g.level || 2) + 1,
                        key: v,
                        neighborType: g.neighborType,
                        entityType: p[0],
                        entityDomain: p[1],
                        entityId: p.slice(2).join('~') || v,
                        clusterKey: g.clusterKey,
                        idCountInNode: 1,
                        isShared: (g.sharedIds || []).includes(v),
                        movedCount: (g.movedIds || []).includes(v) ? 1 : 0,
                        parentGroup: g.id,
                    })
                })
                if (ids.length > shown.length) {
                    const hidden = ids.slice(shown.length)
                    out.push({
                        id: `${g.id}|x|__bundle__`,
                        type: 'entity_bundle',
                        level: (g.level || 2) + 1,
                        key: `+${hidden.length}`,
                        neighborType: g.neighborType,
                        clusterKey: g.clusterKey,
                        bundleTotal: ids.length,
                        bundleDisplayed: shown.length,
                        bundleOverflow: hidden.length,
                        bundleHiddenAllIds: hidden,
                        bundleHiddenPreview: hidden.slice(0, 250),
                        bundleHiddenNotListed: Math.max(0, hidden.length - 250),
                        idCountInNode: hidden.length,
                        parentGroup: g.id,
                    })
                }
            })
            return out
        },
        /** Edges from an opened group to the ID circles it revealed. */
        expansionEdges(nodes) {
            return nodes
                .filter((n) => n.parentGroup)
                .map((n) => ({ source: n.parentGroup, target: n.id, linkKind: 'member' }))
        },
        toggleExpand(id) {
            const next = new Set(this.expandedIds)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            this.expandedIds = next
            this.updateStats()
            this.drawGraph()
        },
        toggleType(type) {
            const next = new Set(this.hiddenTypes)
            if (next.has(type)) next.delete(type)
            else next.add(type)
            this.hiddenTypes = next
            this.updateStats()
            this.drawGraph()
        },
        toggleOrbits() {
            this.showOrbits = !this.showOrbits
            if (this.svg) this.svg.selectAll('.cfg-col-head').attr('opacity', this.showOrbits ? 1 : 0)
        },

        /* ── Radial layout ────────────────────────────────────────────
         * Level 1 nodes own an angular wedge sized by how many children
         * they carry, so a dense cluster gets the arc it needs and a
         * sparse one does not waste it. Children fan out inside their
         * parent's wedge across as many sub-rings as density requires.
         */
        /* ── Balloon layout ──────────────────────────────────────────
         * The Graph Curation page draws a hub with its neighbours fanned around
         * it, and that is the shape people here already read. This nests the
         * same idea: your entity is the centre hub, each cluster is a hub on a
         * ring around it, and every cluster's neighbours fan around their own
         * cluster — pointing outward, away from the centre, so a cluster and
         * its neighbours stay a visually separate group.
         */
        computeLayout(nodes, edges, W, H) {
            const TAU = Math.PI * 2
            const NODE_ARC = 66 /* room one satellite needs on its local ring */

            const byId = new Map(nodes.map((n) => [n.id, n]))
            const children = new Map()
            edges.forEach((e) => {
                const sid = this.eid(e.source)
                const tid = this.eid(e.target)
                const sn = byId.get(sid)
                const tn = byId.get(tid)
                if (!sn || !tn || tn.level !== sn.level + 1) return
                if (!children.has(sid)) children.set(sid, [])
                children.get(sid).push(tid)
            })

            const cx = W / 2
            const cy = H / 2
            const level1 = nodes.filter((n) => n.level === 1)
            const kidsOf = level1.map((n) => (children.get(n.id) || []).map((id) => byId.get(id)).filter(Boolean))

            const localR = kidsOf.map((kids) =>
                kids.length ? Math.max(96, (kids.length * NODE_ARC) / (TAU * 0.62)) : 0
            )
            const maxLocal = Math.max(0, ...localR)
            /* Shared members are laid out along the root->cluster run, so that
               run has to be long enough to seat them. Capped, so one wide-open
               cluster does not throw the picture off screen — anything over the
               cap wraps into extra lanes below. */
            const maxShared = Math.max(0, ...kidsOf.map((kids) => kids.filter((k) => k.isShared).length))
            const n1 = Math.max(1, level1.length)
            const r1 = Math.max(300, ((maxLocal + 70) * n1) / Math.PI, Math.min(1000, maxShared * 46))

            const root = nodes.find((n) => n.level === 0)
            if (root) {
                root.fx = cx
                root.fy = cy
                root.tx = cx
                root.ty = cy
            }

            /* Fan a node's children around it, aimed along `outward` (the
               direction away from the centre) so a branch never doubles back
               over its own parent. Recurses, so an opened group or connector
               gets the same treatment one ring further out. */
            const kidsOfNode = (n) => (children.get(n.id) || []).map((id) => byId.get(id)).filter(Boolean)
            const fan = (parent, outward, only) => {
                const kids = only || kidsOfNode(parent)
                if (!kids.length) return
                const R = Math.max(96, (kids.length * NODE_ARC) / (TAU * 0.62))
                const spread = Math.min(TAU * 0.62, (kids.length * NODE_ARC) / R)
                const start = outward - spread / 2
                kids.forEach((k, j) => {
                    const frac = kids.length === 1 ? 0.5 : j / (kids.length - 1)
                    const a = start + spread * frac
                    k.tx = parent.tx + Math.cos(a) * R
                    k.ty = parent.ty + Math.sin(a) * R
                    fan(k, a)
                })
            }

            /* Shared members sit alongside the root->cluster run, spread along
               it and pushed off to one side — always the same side, so with
               several clusters they fill the gaps evenly instead of piling up.
               Every one carries a two-line caption, so each needs real room
               along the run. When more want a slot than the run can seat, they
               wrap into a further lane instead of stacking on top of each
               other — which is exactly what "All neighbours" used to draw. */
            const placeShared = (cluster, kids) => {
                if (!kids.length) return
                const SEP = 56 /* along the run: a node plus its two caption lines */
                const LANE = 118 /* sideways gap between wrapped lanes */
                const FROM = 0.2
                const TO = 0.82
                const len = Math.hypot(cluster.tx - cx, cluster.ty - cy) || 1
                const ux = (cluster.tx - cx) / len
                const uy = (cluster.ty - cy) / len
                const px = -uy
                const py = ux
                const base = Math.max(150, len * 0.34)
                const perp = Math.atan2(py, px)
                const perLane = Math.max(1, Math.floor((len * (TO - FROM)) / SEP) + 1)
                kids.forEach((k, j) => {
                    const lane = Math.floor(j / perLane)
                    const idx = j % perLane
                    const inLane = Math.min(perLane, kids.length - lane * perLane)
                    const frac = inLane === 1 ? (FROM + TO) / 2 : FROM + ((TO - FROM) * idx) / (inLane - 1)
                    const off = base + lane * LANE
                    k.tx = cx + ux * len * frac + px * off
                    k.ty = cy + uy * len * frac + py * off
                    /* Opened IDs fan further out sideways, away from both ends. */
                    fan(k, perp)
                })
            }

            level1.forEach((n, i) => {
                const a = -Math.PI / 2 + (TAU * i) / n1
                n.tx = cx + Math.cos(a) * r1
                n.ty = cy + Math.sin(a) * r1
                const kids = kidsOfNode(n)
                /* Members the root also knows carry a second edge back to it.
                   Fanned outward with the rest they land on the root->cluster
                   line, and all three edges print on top of each other — the
                   picture reads as one straight run of nodes. They go beside
                   that line instead, so root, cluster and member draw a
                   triangle you can actually follow. */
                fan(n, a, kids.filter((k) => !k.isShared))
                placeShared(n, kids.filter((k) => k.isShared))
            })

            nodes.forEach((n) => {
                if (n.tx == null) {
                    n.tx = cx
                    n.ty = cy
                }
                if (n.x == null) {
                    n.x = n.tx
                    n.y = n.ty
                }
            })

            const drawn = nodes.length
            return { cx, cy, r1, labelMembers: drawn <= 90, balloon: true }
        },

        /* ── Draw ─────────────────────────────────────────────────── */
        drawGraph() {
            const container = this.$refs.graphContainer
            if (!container || !this.hasGraph) return

            const W = container.clientWidth || 900
            const H = container.clientHeight || 600
            const isDark = this.isDark
            const vm = this

            const nodes = this.visibleNodes().map((n) => ({ ...n }))
            const ids = new Set(nodes.map((n) => n.id))
            const edges = this.edges
                .filter((e) => ids.has(this.eid(e.source)) && ids.has(this.eid(e.target)))
                .map((e) => ({ ...e }))
            this.expansionEdges(nodes).forEach((e) => edges.push(e))

            const geo = this.computeLayout(nodes, edges, W, H)

            /* Anything the user dragged keeps the spot they chose. */
            nodes.forEach((n) => {
                const pin = this.pinnedPos.get(n.id)
                if (!pin) return
                n.x = n.fx = n.tx = pin.x
                n.y = n.fy = n.ty = pin.y
                n.pinned = true
            })
            const nr = (d) => this.nodeRadius(d)

            d3.select(container).select('svg').remove()
            const svg = d3
                .select(container)
                .append('svg')
                .attr('width', '100%')
                .attr('height', '100%')
                .attr('viewBox', `0 0 ${W} ${H}`)
            this.svg = svg

            const defs = svg.append('defs')
            const shadowId = `${this.uid}-shadow`
            defs.append('filter')
                .attr('id', shadowId)
                .attr('x', '-60%')
                .attr('y', '-60%')
                .attr('width', '220%')
                .attr('height', '220%')
                .append('feDropShadow')
                .attr('dx', 0)
                .attr('dy', 2)
                .attr('stdDeviation', isDark ? 5 : 3.5)
                .attr('flood-color', isDark ? 'rgba(0,0,0,0.65)' : 'rgba(15,23,42,0.20)')
                .attr('flood-opacity', 1)

            const glowId = `${this.uid}-glow`
            const glow = defs
                .append('filter')
                .attr('id', glowId)
                .attr('x', '-80%')
                .attr('y', '-80%')
                .attr('width', '260%')
                .attr('height', '260%')
            glow.append('feGaussianBlur').attr('stdDeviation', 7).attr('result', 'b')
            const merge = glow.append('feMerge')
            merge.append('feMergeNode').attr('in', 'b')
            merge.append('feMergeNode').attr('in', 'SourceGraphic')

            const g = svg.append('g')

            this.zoomBehavior = d3
                .zoom()
                .scaleExtent([0.05, 2.6])
                .on('zoom', (ev) => g.attr('transform', ev.transform))
            svg.call(this.zoomBehavior).on('dblclick.zoom', null)

            svg.on('click', () => {
                this.hideInspector()
                this.clearHighlight(g)
                this.detailNode = null
            })

            /* Straight links, as on the Graph Curation page — a balloon layout
               already separates the levels by distance, so routing adds nothing. */
            const linkPath = (e) => `M${e.source.x},${e.source.y} L${e.target.x},${e.target.y}`

            /* Your entity -> a cluster whose final_graph_map lists your entity itself
               draws thick. Every other root -> cluster and cluster -> member line
               ('other') draws thin. Dashed and own-branch edges keep their style. */
            const nodeById = new Map(nodes.map((n) => [n.id, n]))
            const mappingKind = (e) => {
                if (e.linkKind !== 'member') return null
                const src = nodeById.get(this.eid(e.source))
                const tgt = nodeById.get(this.eid(e.target))
                if (!src || !tgt) return null
                return src.type === 'root' && tgt.type === 'cluster' && tgt.includesSelf ? 'shared' : 'other'
            }

            const link = g
                .append('g')
                .attr('class', 'cfg-links')
                .selectAll('path')
                .data(edges)
                .join('path')
                .attr('fill', 'none')
                .attr('stroke', (e) => (mappingKind(e) === 'shared' ? SHARED_COLOR : this.edgeColor(e)))
                .attr('stroke-width', (e) => {
                    const m = mappingKind(e)
                    if (m === 'shared') return 3.4
                    if (m === 'other') return 1
                    return e.linkKind === 'direct' ? 2.2 : e.linkKind === 'own' ? 2 : 1.6
                })
                .attr('stroke-opacity', (e) => {
                    const m = mappingKind(e)
                    if (m === 'other') return 0.6
                    return e.linkKind === 'direct' || m === 'shared' ? 0.95 : 0.85
                })
                .attr('stroke-dasharray', (e) => (e.linkKind === 'direct' ? '5 5' : ''))
                .attr('stroke-linecap', 'round')
                .attr('class', 'cfg-link')

            /* Merge curation drop zone: the nearest cluster other than the one the
               node is drawn under, within reach of its circle. The reach is held to
               at least ~90 screen px — a large graph fits to screen zoomed far out,
               and a reach in graph units alone shrank to a few pixels there. */
            let dropTarget = null
            const nearestOtherCluster = (ev, d) => {
                const k = d3.zoomTransform(svg.node()).k || 1
                let best = null
                let bestDist = Infinity
                nodes.forEach((n) => {
                    if (n.type !== 'cluster' || n.key === d.clusterKey || n.x == null) return
                    const dist = Math.hypot(ev.x - n.x, ev.y - n.y)
                    const reach = Math.max(nr(n) + 70, 90 / k)
                    if (dist < reach && dist < bestDist) {
                        best = n
                        bestDist = dist
                    }
                })
                return best
            }
            const setDropTarget = (t) => {
                if (t === dropTarget) return
                dropTarget = t
                g.selectAll('.cfg-node')
                    .filter((n) => n.type === 'cluster')
                    .select('.cfg-node-circle')
                    .attr('stroke', (n) =>
                        n === t ? MOVED_COLOR : isDark ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.9)'
                    )
                    .attr('stroke-width', (n) => (n === t ? 7 : 2))
            }

            /* ── Nodes ── */
            const node = g
                .append('g')
                .attr('class', 'cfg-nodes')
                .selectAll('g')
                .data(nodes, (d) => d.id)
                .join('g')
                .attr('class', 'cfg-node')
                .style('cursor', (d) => (this.enableNodeMove && this.moveKeysOf(d).length ? 'grab' : 'pointer'))
                .call(
                    d3
                        .drag()
                        .on('start', (ev, d) => {
                            if (!ev.active) this.simulation.alphaTarget(0.18).restart()
                            d.fx = d.x
                            d.fy = d.y
                            dropTarget = null
                        })
                        .on('drag', (ev, d) => {
                            d.fx = ev.x
                            d.fy = ev.y
                            if (this.enableNodeMove && this.moveKeysOf(d).length) {
                                setDropTarget(nearestOtherCluster(ev, d))
                            }
                        })
                        .on('end', (ev, d) => {
                            if (!ev.active) this.simulation.alphaTarget(0)
                            const target = dropTarget
                            setDropTarget(null)
                            if (target) {
                                /* The parent redraws with the id under its new cluster,
                                   so the node is not pinned where it was let go. */
                                d.fx = null
                                d.fy = null
                                this.$emit('node-moved', {
                                    keys: this.moveKeysOf(d),
                                    fromCluster: d.clusterKey || '',
                                    toCluster: target.key,
                                    node: { ...d },
                                })
                                return
                            }
                            /* Leave it where it was dropped. Releasing fx/fy let
                               the positional force haul the node straight back
                               to its computed slot, so dragging looked broken.
                               "Re-run layout" puts everything back. */
                            d.fx = ev.x
                            d.fy = ev.y
                            d.pinned = true
                            this.pinnedPos.set(d.id, { x: ev.x, y: ev.y })
                        })
                )

            /* Halo behind the root; a dashed amber ring marks a neighbour the
               root shares with the cluster. */
            node.filter((d) => d.type === 'root')
                .append('circle')
                .attr('class', 'cfg-halo')
                .attr('r', (d) => nr(d) + 12)
                .attr('fill', 'none')
                .attr('stroke', ROOT_COLOR)
                .attr('stroke-width', 1.5)
                .attr('opacity', 0.35)

            node.filter((d) => d.isShared && d.type !== 'cluster')
                .append('circle')
                .attr('r', (d) => nr(d) + 5)
                .attr('fill', 'none')
                .attr('stroke', SHARED_COLOR)
                .attr('stroke-width', 2)
                .attr('stroke-dasharray', '3 3')
                .attr('opacity', 0.9)

            /* A cluster often contains the entity you looked up. Ring it in the
               root's own colour so the repeat reads as "this is you", not as a
               second unrelated node. */
            node.filter((d) => d.isSelf || d.containsSelf || d.includesSelf)
                .append('circle')
                .attr('r', (d) => nr(d) + 5)
                .attr('fill', 'none')
                .attr('stroke', ROOT_COLOR)
                .attr('stroke-width', 2.5)
                .attr('opacity', 0.95)

            node.append('circle')
                .attr('class', 'cfg-node-circle')
                .attr('r', (d) => nr(d))
                .attr('fill', (d) => this.nodeFill(d))
                .attr('stroke', isDark ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.9)')
                .attr('stroke-width', (d) => (d.type === 'root' ? 3 : 2))
                .attr('filter', (d) => (d.type === 'root' ? `url(#${glowId})` : `url(#${shadowId})`))

            /* Ids the user moved into this cluster, not yet saved. */
            node.filter((d) => d.movedCount > 0)
                .append('circle')
                .attr('r', (d) => nr(d) + 5)
                .attr('fill', 'none')
                .attr('stroke', MOVED_COLOR)
                .attr('stroke-width', 2.5)
                .attr('stroke-dasharray', '4 3')

            node.filter((d) => d.pinned)
                .append('circle')
                .attr('r', (d) => nr(d) + 9)
                .attr('fill', 'none')
                .attr('stroke', isDark ? '#4a5568' : '#c3ccd8')
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '2 4')

            node.append('text')
                .attr('class', 'cfg-glyph')
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'central')
                .attr('fill', '#fff')
                .attr('font-family', '"Font Awesome 6 Free", "Font Awesome 5 Free", FontAwesome')
                .attr('font-weight', 900)
                .attr('font-size', (d) => Math.max(9, nr(d) * 0.72))
                .attr('pointer-events', 'none')
                .text((d) => GLYPH[d.type] || GLYPH.neighbor)

            /* Count badge on nodes that stand for many IDs. */
            const badged = node.filter(
                (d) => (d.type === 'entity_group' || d.type === 'entity_bundle') && Number(d.idCountInNode) > 1
            )
            badged
                .append('circle')
                .attr('cx', (d) => nr(d) * 0.78)
                .attr('cy', (d) => -nr(d) * 0.78)
                .attr('r', 9.5)
                .attr('fill', isDark ? '#0f1319' : '#ffffff')
                .attr('stroke', (d) => this.nodeFill(d))
                .attr('stroke-width', 1.6)
            badged
                .append('text')
                .attr('x', (d) => nr(d) * 0.78)
                .attr('y', (d) => -nr(d) * 0.78)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'central')
                .attr('font-size', 8.5)
                .attr('font-weight', 700)
                .attr('pointer-events', 'none')
                .attr('fill', isDark ? '#e2e8f0' : '#334155')
                .text((d) => (d.idCountInNode > 999 ? '999+' : d.idCountInNode))

            /* Every node carries its own two-line caption — type~domain on top,
               the ID beneath. A node that only says "dev~aut" names a category,
               not the neighbour the user came here to find. */
            const caption = (sel, anchor, dx, dy) => {
                const g2 = sel.append('g').attr('class', 'cfg-node-label').attr('pointer-events', 'none')
                const halo = (t) =>
                    t.attr('text-anchor', anchor)
                        .attr('paint-order', 'stroke')
                        .attr('stroke', isDark ? '#12161c' : '#f7f9fc')
                        .attr('stroke-width', 3.5)
                        .attr('stroke-linejoin', 'round')
                halo(
                    g2
                        .append('text')
                        .attr('x', (d) => dx(d))
                        .attr('y', (d) => dy(d))
                        .attr('font-size', 10)
                        .attr('font-weight', 700)
                        .attr('fill', (d) => this.nodeFill(d))
                        .text((d) => this.typeDomainLabel(d))
                )
                halo(
                    g2
                        .append('text')
                        .attr('x', (d) => dx(d))
                        .attr('y', (d) => dy(d) + 12)
                        .attr('font-size', 10)
                        .attr('font-weight', 500)
                        .attr('fill', isDark ? '#aab4c2' : '#5c6878')
                        .text((d) => this.idLabel(d))
                )
            }

            caption(node, 'middle', () => 0, (d) => nr(d) + 15)

            node.on('mouseenter', (ev, d) => {
                this.showInspector(d)
                this.highlight(g, d)
            })
                .on('mouseleave', () => {
                    this.inspectorTimer = setTimeout(() => this.hideInspector(), 120)
                    this.clearHighlight(g)
                })
                .on('click', (ev, d) => {
                    ev.stopPropagation()
                    /* A group of IDs opens into its own ring of circles rather
                       than only filling the side panel. */
                    if ((d.type === 'entity_group' || d.type === 'connector') && (d.entityIds || []).length) {
                        this.toggleExpand(d.id)
                        return
                    }
                    this.detailNode = d
                    this.$emit('node-select', d)
                })

            /* ── Relaxation ──
               The columns are the layout; the simulation only resolves overlap
               and keeps dragging feeling live, so the positional pull is strong
               enough that nodes stay on their grid slot. */
            const paint = () => {
                link.attr('d', linkPath)
                node.attr('transform', (d) => `translate(${d.x},${d.y})`)
            }

            if (this.simulation) this.simulation.stop()
            this.simulation = d3
                .forceSimulation(nodes)
                /* Padded well past the circle: every node carries a two-line
                   caption underneath, and a radius that only covers the circle
                   let those captions print over each other. */
                .force('collide', d3.forceCollide().radius((d) => nr(d) + 11).iterations(2))
                .force('x', d3.forceX((d) => d.tx).strength(0.9))
                .force('y', d3.forceY((d) => d.ty).strength(0.9))
                .alpha(0.6)
                .alphaDecay(0.09)

            /* forceLink resolves the string endpoints to node objects, so the
               very first paint can happen before a single tick has run. The
               graph must be correct even where rAF is throttled (background
               tab, reduced motion) and the simulation never advances. */
            edges.forEach((e) => {
                if (typeof e.source === 'string') e.source = nodes.find((n) => n.id === e.source) || e.source
                if (typeof e.target === 'string') e.target = nodes.find((n) => n.id === e.target) || e.target
            })
            paint()
            this.simulation.on('tick', paint)
            this.simulation.on('end', () => this.fitToScreen())

            /* Staggered reveal, inside-out, so the story reads root -> cluster ->
               neighbour. Driven by CSS rather than a d3 transition: a CSS
               animation with fill-mode `both` still ends at opacity 1 when
               animations are disabled, where a stalled transition would leave
               the whole graph invisible. */
            node.style('animation-delay', (d) => `${(d.level || 0) * 130}ms`)
            link.style('animation-delay', '180ms')

            this.fitToScreen(false)
            setTimeout(() => this.fitToScreen(), 700)
            vm.updateStats()
        },

        /* ── Interaction ──────────────────────────────────────────── */
        highlight(g, d) {
            g.selectAll('.cfg-node').style('opacity', (n) => (this.isConnected(n, d) ? 1 : 0.22))
            g.selectAll('.cfg-link').style('opacity', (e) =>
                this.eid(e.source) === d.id || this.eid(e.target) === d.id ? 1 : 0.08
            )
        },
        clearHighlight(g) {
            g.selectAll('.cfg-node').style('opacity', 1)
            g.selectAll('.cfg-link').style('opacity', null)
        },
        isConnected(n, d) {
            if (n.id === d.id) return true
            return this.edges.some(
                (e) =>
                    (this.eid(e.source) === d.id && this.eid(e.target) === n.id) ||
                    (this.eid(e.target) === d.id && this.eid(e.source) === n.id)
            )
        },
        showInspector(d) {
            clearTimeout(this.inspectorTimer)
            const lines = []
            if (d.type === 'root') {
                lines.push(`in ${d.clusterCount} cluster${d.clusterCount === 1 ? '' : 's'}`, `${d.flattenCount} direct neighbour${d.flattenCount === 1 ? '' : 's'}`)
            } else if (d.type === 'cluster') {
                lines.push(`${d.neighborCount} members`)
                if (d.includesSelf) lines.push('one of them is your entity')
                if (d.sharedCount) lines.push(`${d.sharedCount} also your neighbour${d.sharedCount === 1 ? '' : 's'}`)
            } else if (d.type === 'connector') {
                lines.push(`${(d.entityIds || []).length} shared neighbour${(d.entityIds || []).length === 1 ? '' : 's'}`)
                lines.push('yours and the cluster\u2019s — this is the link')
                lines.push(this.expandedIds.has(d.id) ? 'click to collapse' : 'click to open')
            } else if (d.type === 'entity_group') {
                lines.push(`${(d.entityIds || []).length} IDs`)
                if (d.isOwnGroup) lines.push('nodes off your entity')
                if (d.containsSelf) lines.push('includes the entity you looked up')
                lines.push(this.expandedIds.has(d.id) ? 'click to collapse' : 'click to open')
                if ((d.sharedIds || []).length) lines.push(`${d.sharedIds.length} also your neighbour`)
            } else if (d.type === 'entity_bundle') {
                lines.push(`${d.bundleOverflow} collapsed of ${d.bundleTotal}`)
            } else {
                if (d.isSelf) lines.push('this is the entity you looked up')
                if (d.isOwnOnly) lines.push('node off your entity')
                if (d.isShared) lines.push('also a direct neighbour of your entity')
                if (d.clusterKey) lines.push(`in ${d.clusterKey}`)
            }
            this.inspector = {
                visible: true,
                label: d.key || d.id,
                color: this.nodeFill(d),
                lines,
            }
        },
        hideInspector() {
            clearTimeout(this.inspectorTimer)
            this.inspector = { ...this.inspector, visible: false }
        },

        /* ── View controls ────────────────────────────────────────── */
        zoomIn() {
            if (this.svg) this.svg.transition().duration(220).call(this.zoomBehavior.scaleBy, 1.35)
        },
        zoomOut() {
            if (this.svg) this.svg.transition().duration(220).call(this.zoomBehavior.scaleBy, 0.72)
        },
        fitToScreen(animate = true) {
            const container = this.$refs.graphContainer
            if (!this.svg || !container) return
            const gEl = this.svg.select('g').node()
            if (!gEl) return
            let box
            try {
                box = gEl.getBBox()
            } catch (_) {
                return
            }
            if (!box.width || !box.height) return
            const W = container.clientWidth || 900
            const H = container.clientHeight || 600
            const pad = 32
            const scale = Math.min((W - pad) / box.width, (H - pad) / box.height, 1.6)
            const tx = W / 2 - scale * (box.x + box.width / 2)
            const ty = H / 2 - scale * (box.y + box.height / 2)
            const transform = d3.zoomIdentity.translate(tx, ty).scale(scale)
            if (animate) this.svg.transition().duration(420).call(this.zoomBehavior.transform, transform)
            else this.svg.call(this.zoomBehavior.transform, transform)
        },
        resetView() {
            /* Drop every manual position so the computed layout applies again. */
            this.pinnedPos.clear()
            this.drawGraph()
        },
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen
            this.$nextTick(() => setTimeout(() => this.drawGraph(), 120))
        },
        download(name, text) {
            const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = name
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        },
    },
}
</script>

<style scoped>
.cfg-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    border-radius: inherit;
}
.cfg-fullscreen {
    position: fixed !important;
    inset: 0;
    z-index: 1050;
    border-radius: 0 !important;
    background: #fff;
}

/* ── Toolbar: an instrument readout, not a button bar ─────────────── */
.cfg-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 9px 16px;
    background: #fff;
    border-bottom: 1px solid #e6eaf0;
    flex-shrink: 0;
}
.cfg-tb-right {
    display: flex;
    align-items: center;
    gap: 4px;
}
.cfg-tb-sep {
    width: 1px;
    height: 18px;
    background: #e6eaf0;
    margin: 0 4px;
}
.cfg-tb-btn {
    width: 30px;
    height: 30px;
    border: 1px solid #e6eaf0;
    border-radius: 8px;
    background: transparent;
    color: #64748b;
    font-size: 12.5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.13s, color 0.13s, border-color 0.13s;
}
.cfg-tb-btn:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #cbd5e1;
}
.cfg-tb-btn.is-on {
    background: #eef2ff;
    border-color: #c7d2fe;
    color: #4338ca;
}

.cfg-readout {
    display: flex;
    align-items: stretch;
    gap: 0;
    flex-wrap: wrap;
}
.cfg-readout-cell {
    display: flex;
    flex-direction: column;
    padding: 0 14px;
    border-left: 1px solid #eef1f5;
    line-height: 1.15;
}
.cfg-readout-cell:first-child {
    border-left: 0;
    padding-left: 2px;
}
.cfg-readout-cell b {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
}
.cfg-readout-cell i {
    font-style: normal;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: #94a3b8;
    font-weight: 600;
}
.cfg-readout-cell--shared b {
    color: #b45309;
}

/* ── Legend ───────────────────────────────────────────────────────── */
.cfg-legend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 16px;
    background: #fff;
    border-bottom: 1px solid #e6eaf0;
    flex-shrink: 0;
}
.cfg-legend-static {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: #64748b;
    padding: 3px 2px;
}
.cfg-legend-div {
    width: 1px;
    height: 16px;
    background: #e6eaf0;
    margin: 0 6px;
}
.cfg-legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.cfg-legend-dot--root {
    background: #0f766e;
}
.cfg-legend-dot--cluster {
    background: #6f42c1;
}
.cfg-legend-dot--moved {
    background: transparent;
    border: 2px dashed #10b981;
}
.cfg-legend-swatch-link {
    width: 18px;
    height: 0;
    border-top: 3px dashed #d97706;
    position: relative;
    flex-shrink: 0;
}
.cfg-legend-swatch-link::after {
    content: '';
    position: absolute;
    left: 5px;
    top: -5px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d97706;
}
/* Solid teal run — the root's own branch, as opposed to the dashed amber
   link into a cluster. */
.cfg-legend-swatch-own {
    width: 18px;
    height: 0;
    border-top: 3px solid #0f766e;
    position: relative;
    flex-shrink: 0;
}
.cfg-legend-swatch-own::after {
    content: '';
    position: absolute;
    left: 5px;
    top: -5px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #0f766e;
}
.cfg-legend-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 11px;
    border: 1px solid #e6eaf0;
    border-radius: 20px;
    background: #fff;
    cursor: pointer;
    font-size: 11.5px;
    color: #334155;
    transition: all 0.14s;
}
.cfg-legend-chip:hover {
    border-color: #94a3b8;
    transform: translateY(-1px);
}
.cfg-legend-chip.is-off {
    opacity: 0.4;
    background: #f8fafc;
    transform: none;
}
.cfg-legend-label {
    font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
}
.cfg-legend-cnt {
    color: #94a3b8;
    font-size: 10.5px;
    font-variant-numeric: tabular-nums;
}
.cfg-legend-more {
    border-style: dashed;
    color: #64748b;
    gap: 4px;
}
.cfg-legend-more i {
    font-size: 8px;
    opacity: 0.7;
}

/* ── Canvas ───────────────────────────────────────────────────────── */
.cfg-canvas-area {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.cfg-canvas {
    position: relative;
    flex: 1;
    width: 100%;
    min-height: 0;
    cursor: grab;
    background-color: #f7f9fc;
    background-image: radial-gradient(circle at 50% 50%, rgba(15, 118, 110, 0.05) 0%, transparent 55%),
        radial-gradient(circle, #d7dee8 1px, transparent 1px);
    background-size: 100% 100%, 26px 26px;
}
.cfg-canvas:active {
    cursor: grabbing;
}
.cfg-empty {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #94a3b8;
    pointer-events: none;
}
.cfg-empty i {
    font-size: 34px;
    opacity: 0.35;
}
.cfg-empty p {
    margin: 0;
    font-size: 13px;
}

/* ── Inspector ────────────────────────────────────────────────────── */
.cfg-inspector {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(6px);
    border-bottom: 1px solid #e6eaf0;
    border-left: 3px solid #0f766e;
    font-size: 12px;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.06);
    pointer-events: none;
}
.cfg-insp-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: color-mix(in srgb, var(--ic, #0f766e) 12%, transparent);
    border: 1px solid color-mix(in srgb, var(--ic, #0f766e) 34%, transparent);
    border-radius: 20px;
    padding: 2px 10px 2px 5px;
    font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11.5px;
    max-width: 460px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.cfg-insp-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--ic, #0f766e);
    flex-shrink: 0;
}
.cfg-insp-meta {
    color: #64748b;
}
.cfg-insp-sep {
    margin: 0 5px;
    opacity: 0.5;
}
.cfg-insp-enter-active,
.cfg-insp-leave-active {
    transition: transform 0.15s ease, opacity 0.15s ease;
}
.cfg-insp-enter-from,
.cfg-insp-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}

/* ── Detail panel ─────────────────────────────────────────────────── */
.cfg-panel {
    position: absolute;
    top: 92px;
    right: 14px;
    width: 320px;
    max-height: calc(100% - 120px);
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #e6eaf0;
    border-radius: 12px;
    box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16), 0 3px 10px rgba(15, 23, 42, 0.08);
    z-index: 20;
    overflow: hidden;
}
.cfg-panel--wide {
    width: 396px;
}
.cfg-panel-hdr {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 11px 12px;
    background: var(--node-color, #0f766e);
    color: #fff;
}
.cfg-panel-badge {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
}
.cfg-panel-title {
    font-size: 12.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    flex: 1;
}
.cfg-panel-close {
    border: 0;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
}
.cfg-panel-close:hover {
    background: rgba(255, 255, 255, 0.3);
}
.cfg-panel-body {
    padding: 12px;
    overflow-y: auto;
    font-size: 12px;
}
.cfg-panel-note {
    font-size: 11.5px;
    color: #64748b;
    margin: 0 0 9px;
    line-height: 1.5;
}
.cfg-panel-note--amber {
    color: #b45309;
}
.cfg-panel-note--teal {
    color: #0f766e;
}
.cfg-mono-inline {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 10.5px;
    background: #f1f5f9;
    padding: 1px 5px;
    border-radius: 4px;
    word-break: break-all;
}
.cfg-sec-lbl {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    font-weight: 700;
    color: #94a3b8;
    margin: 11px 0 5px;
}
.cfg-sec-lbl:first-child {
    margin-top: 0;
}
.cfg-mono-val {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
    background: #f7f9fc;
    border: 1px solid #eef1f5;
    border-radius: 6px;
    padding: 7px 9px;
    word-break: break-all;
    color: #0f172a;
}
.cfg-kv {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 5px 0;
    border-bottom: 1px dashed #eef1f5;
}
.cfg-kv span:first-child {
    color: #94a3b8;
    font-size: 11px;
    font-weight: 600;
}
.cfg-kv span:last-child {
    font-weight: 600;
    color: #0f172a;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
}
.cfg-pill-row {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 9px;
}
.cfg-type-pill {
    background: #eef2ff;
    border: 1px solid #dfe5fb;
    color: #4338ca;
    border-radius: 20px;
    padding: 2px 9px;
    font-size: 10.5px;
    font-weight: 700;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.cfg-cnt-badge {
    background: #f1f5f9;
    color: #64748b;
    border-radius: 20px;
    padding: 2px 9px;
    font-size: 10.5px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}
.cfg-flag {
    border-radius: 20px;
    padding: 2px 9px;
    font-size: 10.5px;
    font-weight: 700;
}
.cfg-flag--amber {
    background: #fef3c7;
    color: #b45309;
}
.cfg-flag--slate {
    background: #f1f5f9;
    color: #64748b;
}
.cfg-flag--teal {
    background: #ccfbf1;
    color: #0f766e;
}
.cfg-amber {
    color: #b45309;
}
.cfg-stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(84px, 1fr));
    gap: 6px;
    margin: 10px 0;
}
.cfg-stat-cell {
    background: #f7f9fc;
    border: 1px solid #eef1f5;
    border-radius: 8px;
    padding: 7px 9px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.cfg-stat-cell span {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    font-weight: 700;
}
.cfg-stat-cell b {
    font-size: 16px;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
}
.cfg-id-list {
    max-height: 230px;
    overflow-y: auto;
    background: #f7f9fc;
    border: 1px solid #eef1f5;
    border-radius: 7px;
}
.cfg-id-row {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 10.5px;
    padding: 5px 9px;
    border-bottom: 1px solid #eef1f5;
    word-break: break-all;
    color: #334155;
}
.cfg-id-row:last-child {
    border-bottom: 0;
}
.cfg-id-row--shared {
    background: #fffbeb;
    color: #92400e;
    border-left: 2px solid #d97706;
}
.cfg-btn-row {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
}
.cfg-slide-enter-active,
.cfg-slide-leave-active {
    transition: transform 0.2s ease, opacity 0.2s ease;
}
.cfg-slide-enter-from,
.cfg-slide-leave-to {
    transform: translateX(16px);
    opacity: 0;
}
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

/* Dashed direct-neighbour edges crawl so they read as a different kind of
   link from the plain hierarchy edges. */
@keyframes cfgFlow {
    to {
        stroke-dashoffset: -20;
    }
}
@keyframes cfgIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
.cfg-node,
.cfg-link {
    animation: cfgIn 0.34s ease both;
}
.cfg-link--flow {
    animation: cfgFlow 1.1s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
    .cfg-link--flow,
    .cfg-node,
    .cfg-link {
        animation: none;
    }
}

body.dark-mode {
    .cfg-fullscreen { background: $dark-card-bg; }
    .cfg-toolbar,
    .cfg-legend {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }
    .cfg-tb-btn {
        color: $dark-text-muted;
        border-color: $dark-border-color;
    }
    .cfg-tb-btn:hover {
        background: $dark-border-color;
        color: $dark-text-primary;
    }
    .cfg-tb-btn.is-on {
        background: #1e2a4a;
        border-color: #2d3f6d;
        color: #a5b4fc;
    }
    .cfg-tb-sep,
    .cfg-legend-div { background: $dark-border-color; }
    .cfg-readout-cell { border-left-color: $dark-border-color; }
    .cfg-readout-cell b { color: $dark-text-primary; }
    .cfg-readout-cell i { color: $dark-text-muted; }
    .cfg-readout-cell--shared b { color: #fbbf24; }
    .cfg-legend-static { color: $dark-text-muted; }
    .cfg-legend-chip {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;
    }
    .cfg-legend-chip:hover { border-color: #4a5568; }
    .cfg-legend-chip.is-off { background: $dark-body-bg; }
    .cfg-legend-cnt { color: $dark-text-muted; }

    .cfg-canvas {
        background-color: $dark-card-bg;
        background-image: radial-gradient(circle at 50% 50%, rgba(45, 212, 191, 0.06) 0%, transparent 55%),
            radial-gradient(circle, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    }
    .cfg-empty { color: $dark-text-muted; }

    .cfg-inspector {
        background: rgba(15, 19, 25, 0.97);
        border-bottom-color: $dark-border-color;
    }
    .cfg-insp-meta { color: $dark-text-muted; }

    .cfg-panel {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        box-shadow: 0 20px 56px rgba(0, 0, 0, 0.65), 0 4px 14px rgba(0, 0, 0, 0.45);
    }
    .cfg-panel-body { color: $dark-text-primary; }
    .cfg-panel-note { color: $dark-text-muted; }
    .cfg-panel-note--amber { color: #fbbf24; }
    .cfg-panel-note--teal { color: #5eead4; }
    .cfg-legend-swatch-own { border-top-color: #2dd4bf; }
    .cfg-legend-swatch-own::after { background: #2dd4bf; }
    .cfg-mono-inline { background: $dark-body-bg; color: $dark-text-primary; }
    .cfg-sec-lbl { color: $dark-text-muted; }
    .cfg-mono-val {
        background: $dark-body-bg;
        border-color: $dark-border-color;
        color: $dark-text-primary;
    }
    .cfg-kv { border-bottom-color: $dark-border-color; }
    .cfg-kv span:first-child { color: $dark-text-muted; }
    .cfg-kv span:last-child { color: $dark-text-primary; }
    .cfg-type-pill { background: #1e2a4a; border-color: #2d3f6d; color: #a5b4fc; }
    .cfg-cnt-badge,
    .cfg-flag--slate { background: $dark-border-color; color: $dark-text-muted; }
    .cfg-flag--teal { background: #0d2b28; color: #5eead4; }
    .cfg-flag--amber { background: #451a03; color: #fbbf24; }
    .cfg-amber { color: #fbbf24; }
    .cfg-stat-cell {
        background: $dark-body-bg;
        border-color: $dark-border-color;
    }
    .cfg-stat-cell span { color: $dark-text-muted; }
    .cfg-stat-cell b { color: $dark-text-primary; }
    .cfg-id-list {
        background: $dark-body-bg;
        border-color: $dark-border-color;
    }
    .cfg-id-row {
        color: $dark-text-primary;
        border-bottom-color: $dark-border-color;
    }
    .cfg-id-row--shared { background: #2a1c05; color: #fbbf24; }
}
</style>
